import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import useMe from '@app/hooks/auth/useMe';
import { IoChevronDown } from 'react-icons/io5';
import { CgProfile } from 'react-icons/cg';
import { Dropdown, Spin, type MenuProps } from 'antd';
import { MdLogout } from 'react-icons/md';
import ImpersonateBanner from './ImpersonateBanner';
import useUserActions from '@app/hooks/users/useUserActions';
import { fn } from '@app/utils';
import { url } from '@app/utils/constants';

export type AuthHeaderProps = {
    onLogout?: () => void;
};

export const AuthHeader = ({ onLogout }: AuthHeaderProps) => {
    const user = useMe();
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const { endImpersonateUser } = useUserActions();
    const { mutate: endImpersonateUserMutate } = endImpersonateUser;
    const impersonatingUser = !!fn.getImpersonationToken();

    const handleEndImpersonation = () => {
        endImpersonateUserMutate(undefined, {
            onSettled: () => {
                fn.clearData('impersonation_token');
                queryClient.invalidateQueries({ queryKey: ['me'] });
                navigate(url.adminPortal.dashboard);
            },
        });
    };

    const items: MenuProps['items'] = [
        {
            key: '1',
            label: (
                <div>
                    <small>
                        <strong>Role</strong>
                    </small>
                    <br />
                    <span>{user.data?.role_name || 'N/A'}</span>
                </div>
            ),
            style: {
                pointerEvents: 'none',
            },
        },
        { type: 'divider' },
        {
            key: '2',
            label: 'Logout',
            icon: <MdLogout />,
            onClick: onLogout,
        },
    ];

    return (
        <div className="auth-header-wrap">
            {impersonatingUser ? (
                <ImpersonateBanner
                    handleEndImpersonation={handleEndImpersonation}
                    impersonatedUser={user?.data}
                />
            ) : (
                <div className="auth-header-bar">
                    <Dropdown
                        menu={{ items }}
                        classNames={{ root: 'styled-dropdown' }}
                        disabled={user.isPending}
                        trigger={['hover', 'click']}>
                        <div className="auth-header-profile">
                            <Spin spinning={user.isPending}>
                                <button className="auth-header-profile-btn">
                                    <CgProfile size={30} />
                                    <div>
                                        <div className="name">
                                            {user.data?.first_name} {user.data?.last_name}
                                        </div>
                                        <span className="email">{user.data?.email}</span>
                                    </div>
                                    <IoChevronDown size={20} />
                                </button>
                            </Spin>
                        </div>
                    </Dropdown>
                </div>
            )}
        </div>
    );
};

export default AuthHeader;
